import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip } from "@mui/material";
import { Delete, Send } from "@mui/icons-material";
import { toLocalDateTimeInputValue, parseLocalDateTimeString } from "./utils";
import { usePreferences } from "../../hooks/usePreferences";
import CustomerSearch from "../customerSearch/customerSearch";
import { EmployeeService } from "../../service/employeeService";
import { LocationService } from "../../service/locationService";
import { useEncounters } from '../../contexts/encountersContext';

export default function BookingDialog({ open, event, mode, onClose, onDelete, onSave, defaultLocationId, defaultPractitionerId }) {
  const { preferences } = usePreferences();
  const tz = preferences?.timezone || "America/Toronto";

  const [form, setForm] = useState({
    practitionerId: event?.practitionerId || defaultPractitionerId,
    locationId: event?.locationId || defaultLocationId,
    patientName: event?.patientName || "",
    customerId: event?.customerId || null,
    customerData: event?.customerData || null,
    notes: event?.notes || "",
    startLocal: event?.startUtc ? toLocalDateTimeInputValue(event.startUtc, tz) : toLocalDateTimeInputValue(new Date(), tz),
    endLocal: event?.endUtc ? toLocalDateTimeInputValue(event.endUtc, tz) : toLocalDateTimeInputValue(new Date(Date.now() + 30 * 60000), tz),
    encounterId: event?.encounterId || null,
  });

  const [customerSearchReset, setCustomerSearchReset] = useState(0);
  const [practitioners, setPractitioners] = useState([]);
  const [locations, setLocations] = useState([]);
  const { encounters } = useEncounters();
  const [selectedEncounterId, setSelectedEncounterId] = useState(event?.encounterId || null);

  useEffect(() => {
    if (open) {
      setForm({
        practitionerId: event?.practitionerId || defaultPractitionerId,
        locationId: event?.locationId || defaultLocationId,
        patientName: event?.patientName || "",
        customerId: event?.customerId || null,
        customerData: event?.customerData || null,
        notes: event?.notes || "",
        startLocal: event?.startUtc ? toLocalDateTimeInputValue(event.startUtc, tz) : toLocalDateTimeInputValue(new Date(), tz),
        endLocal: event?.endUtc ? toLocalDateTimeInputValue(event.endUtc, tz) : toLocalDateTimeInputValue(new Date(Date.now() + 30 * 60000), tz),
        encounterId: event?.encounterId || null,
      });
      setSelectedEncounterId(event?.encounterId || null);
      // Reset customer search when dialog opens
      setCustomerSearchReset(prev => prev + 1);

      // Fetch options for practitioners and locations
      EmployeeService.getPractitioners()
        .then(res => { if (res?.code === 0 && Array.isArray(res.data)) setPractitioners(res.data); })
        .catch(() => setPractitioners([]));
      LocationService.getLocations()
        .then(res => { if (res?.code === 0 && Array.isArray(res.data)) setLocations(res.data); })
        .catch(() => setLocations([]));
    }
  }, [open, event, defaultLocationId, defaultPractitionerId]);

  // When start time changes and an encounter with duration is selected, auto-adjust end
  useEffect(() => {
    if (!selectedEncounterId) return;
    const enc = encounters.find(e => e.id === selectedEncounterId);
    if (!enc || !enc.duration) return;
    const start = parseLocalDateTimeString(form.startLocal, tz);
    const newEnd = new Date(start.getTime() + enc.duration * 60000);
    setForm(prev => ({ ...prev, endLocal: toLocalDateTimeInputValue(newEnd, tz) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startLocal, selectedEncounterId]);

  const handleCustomerSelected = (customer) => {
    if (customer) {
      setForm(prev => ({
        ...prev,
        customerId: customer.id,
        customerData: customer,
        patientName: customer.fullName
      }));
    } else {
      setForm(prev => ({
        ...prev,
        customerId: null,
        customerData: null,
        patientName: ""
      }));
    }
  };

  const handleSave = () => onSave({
    id: event?.id,
    ...form,
    startdate: form.startLocal,
    enddate: form.endLocal
  });
  const handleDelete = () => { if (event?.id) onDelete(event.id); onClose(); };
  const sendReminder = () => { /* mock */ };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "edit" ? "Edit Appointment" : "Book Appointment"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <FormControl size="small">
            <InputLabel id="pr-label">Practitioner</InputLabel>
            <Select labelId="pr-label" label="Practitioner" value={form.practitionerId || ''} onChange={(e) => setForm({ ...form, practitionerId: e.target.value })}>
              {practitioners.length === 0 ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : practitioners.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.fullName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="loc-label">Location</InputLabel>
            <Select labelId="loc-label" label="Location" value={form.locationId || ''} onChange={(e) => setForm({ ...form, locationId: e.target.value })}>
              {locations.length === 0 ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : locations.map(l => (
                <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="enc-label">Encounter</InputLabel>
            <Select
              labelId="enc-label"
              label="Encounter"
              value={selectedEncounterId || ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setSelectedEncounterId(val);
                setForm(prev => ({ ...prev, encounterId: val }));
                if (val) {
                  const enc = encounters.find(en => en.id === val);
                  if (enc && enc.duration) {
                    const start = parseLocalDateTimeString(form.startLocal, tz);
                    const newEnd = new Date(start.getTime() + enc.duration * 60000);
                    setForm(prev => ({ ...prev, endLocal: toLocalDateTimeInputValue(newEnd, tz) }));
                  }
                }
              }}
            >
              {encounters.length === 0 ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : [
                <MenuItem key="none" value="">(None)</MenuItem>,
                ...encounters.map(e => (
                  <MenuItem key={e.id} value={e.id}>{e.name}{e.duration ? ` (${e.duration}m)` : ''}</MenuItem>
                ))
              ]}
            </Select>
          </FormControl>
          <CustomerSearch 
            onCustomerSelected={handleCustomerSelected}
            reset={customerSearchReset}
            sx={{ mb: 1 }}
          />
          {form.patientName && (
            <TextField 
              size="small" 
              label="Selected Patient" 
              value={form.patientName} 
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          )}
          <TextField size="small" label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <TextField size="small" type="datetime-local" label="Start (local)" value={form.startLocal} onChange={(e) => setForm({ ...form, startLocal: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="datetime-local" label="End (local)" value={form.endLocal} onChange={(e) => setForm({ ...form, endLocal: e.target.value })} InputLabelProps={{ shrink: true }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {mode === "edit" && (
          <Tooltip title="Delete">
            <IconButton color="error" onClick={handleDelete}><Delete /></IconButton>
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Send reminder (mock)"><IconButton onClick={sendReminder}><Send /></IconButton></Tooltip>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>{mode === "edit" ? "Update" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
}
